import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '~/components/Dropdown';
import { CheckboxTree } from '~/modules/artist-roster/components/CheckboxTree';
import type { CheckboxTreeItem } from '~/modules/artist-roster/components/CheckboxTree/types';
import { flattenCheckboxTree } from '~/modules/artist-roster/components/CheckboxTree/utils';
import { getLabelHierarchy } from '~/modules/artist-roster/lib/labels';
import { DIRTY } from '~/modules/artist-roster/pages/constants';
import type { LabelData } from '~/modules/artist-roster/types';

import { mapLabelHierarchyToCheckboxTreeItem } from '../utils/mapLabelHierarchyToCheckboxTreeItem';

interface LabelFilterProps {
  originalLabels: LabelData[];
}

export function LabelFilter({ originalLabels }: LabelFilterProps) {
  const { t } = useTranslation('roster');
  const { watch, setValue } = useFormContext();
  const labelFlatItems = watch('labels');
  const [labelTreeItems, setLabelTreeItems] = useState<CheckboxTreeItem[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['labels-hierarchies'],
    queryFn: () => getLabelHierarchy(),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.items) {
      const originalLabelIds = new Set(
        originalLabels.map((label) => label.labelId),
      );

      setLabelTreeItems(
        mapLabelHierarchyToCheckboxTreeItem(data.items, originalLabelIds),
      );
    }
  }, [data, originalLabels]);

  useEffect(() => {
    if (isLoading || !labelTreeItems.length) {
      return;
    }

    const newLabelFlatItems = flattenCheckboxTree(labelTreeItems)
      .filter((label) => label.checked)
      .map((label) => ({
        labelId: parseInt(label.id, 10),
        name: label.label,
      }))
      .sort((a, b) => a.labelId - b.labelId);

    setValue('labels', newLabelFlatItems, DIRTY);
  }, [labelTreeItems, setValue, isLoading, originalLabels]);

  const handleLabelsTreeChange = useCallback((newItems: CheckboxTreeItem[]) => {
    setLabelTreeItems(newItems);
  }, []);

  const onDropdownClose = () => {
    if (labelTreeItems.length) {
      const hasCheckedItems = flattenCheckboxTree(labelTreeItems).some(
        (item) => item.checked,
      );

      if (!hasCheckedItems) {
        const originalLabelIds = new Set(
          originalLabels.map((label) => label.labelId),
        );

        if (data?.items) {
          setLabelTreeItems(
            mapLabelHierarchyToCheckboxTreeItem(data.items, originalLabelIds),
          );
        }
      }
    }
  };

  return (
    <Dropdown
      onClose={onDropdownClose}
      options={[]}
      placeholder={
        labelFlatItems.length > 0
          ? t('filters.labelsWithCount', {
              count: labelFlatItems.length,
            })
          : t('filters.selectLabelRegion')
      }
      contentClassName="z-30"
    >
      <CheckboxTree
        value={labelTreeItems}
        setValue={setLabelTreeItems}
        onChange={handleLabelsTreeChange}
      />
    </Dropdown>
  );
}

LabelFilter.displayName = 'LabelFilter';
